import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import { Box } from "@mui/material";
import { useApi } from "../hooks";
import { apiGetUser, method } from "../apiPath";
import keyNames from "../keyName";
import { getToast } from "../common/utils";
import ErrorBoundary from "../common/ErrorBoundary";
import { tataLogin, tataRefresh, getStoredTataToken } from "../services/tataAuth";

const toast = getToast("error");

const AdminLayout = () => {
  const hitApi = useApi();
  const dispatch = useDispatch();
  const location = useLocation();
  const isDetailPage =
    location.pathname === "/app/agent-management/database/details" ||
    location.pathname === "/app/contacts-engagement/detail";
  const userData = useSelector((state) => state.mainReducer.userData);
  const tataToken = useSelector((state) => state.mainReducer.tataToken);
  const { themeMode } = useSelector((state) => state.mainReducer);
  const [open, setOpen] = useState(false);
  const refreshTimer = useRef(null);

  const dispatchTataToken = (token) => {
    dispatch({ type: keyNames.REUSABLE, keyName: keyNames.tataToken, payload: token });
  };

  // Schedule a refresh 55 min after login (token lasts 60 min)
  const scheduleRefresh = (currentToken, expiryMs) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(expiryMs - Date.now() - 60_000, 5_000); // 1 min before expiry
    refreshTimer.current = setTimeout(async () => {
      try {
        const { token, expiry } = await tataRefresh(currentToken);
        dispatchTataToken(token);
        scheduleRefresh(token, expiry);
      } catch (err) {
        console.warn("[Tata] refresh failed, re-login:", err.message);
        try {
          const { token, expiry } = await tataLogin();
          dispatchTataToken(token);
          scheduleRefresh(token, expiry);
        } catch {}
      }
    }, delay);
  };

  // Init Tata token when user is authenticated (uses agent's SmartFlo credentials)
  useEffect(() => {
    if (!userData) return;
    if (tataToken) return; // already in Redux (e.g. after hot reload)

    const { tataSmartFlowId, tataSmartFlowPassword } = userData;
    if (!tataSmartFlowId || !tataSmartFlowPassword) return; // Tata not configured for this user

    const stored = getStoredTataToken();
    if (stored) {
      dispatchTataToken(stored.token);
      scheduleRefresh(stored.token, stored.expiry);
    } else {
      tataLogin(tataSmartFlowId, tataSmartFlowPassword)
        .then(({ token, expiry }) => {
          dispatchTataToken(token);
          scheduleRefresh(token, expiry);
        })
        .catch((err) => console.warn("[Tata] initial login failed:", err.message));
    }

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userData) {
      hitApi(apiGetUser, null, method.get, keyNames.userData, toast);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarWidth = isDetailPage ? 0 : open ? 200 : 56;

  return (
    userData && (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          overflow: "hidden",
          bgcolor: themeMode === "dark" ? "#0f172a" : "#f1f5f9",
        }}
      >
        {!isDetailPage && <AdminSidebar open={open} setOpen={setOpen} />}

        {/* Main content */}
        <Box
          sx={{
            ml: `${sidebarWidth}px`,
            height: "100vh",
            width: `calc(100vw - ${sidebarWidth}px)`,
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            transition: "margin-left 0.2s ease, width 0.2s ease",
          }}
        >
          {/* Scrollable page area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "auto",
              p: 2,
              height: "100%",
            }}
          >
            <ErrorBoundary>
              <Box key={location.pathname} className="page-enter">
                <Outlet />
              </Box>
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>
    )
  );
};

export default AdminLayout;
